'use client';

import { useMemo, useState } from 'react';
import AppFooter from '@/components/AppFooter';
import AppLogo from '@/components/AppLogo';
import BottomNavigation from '@/components/BottomNavigation';
import TransactionForm, { type TransactionFormPayload } from '@/components/TransactionForm';
import TransactionList from '@/components/TransactionList';
import { useCategories } from '@/hooks/useCategories';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useTags } from '@/hooks/useTags';
import { useTransactions } from '@/hooks/useTransactions';
import { formatMonthKey, formatMonthLabel } from '@/lib/date';
import { formatMoney } from '@/lib/money';

export default function TransactionsPage() {
  const { categories, loading: loadingCategories, error: categoriesError } = useCategories();
  const { tags, loading: loadingTags, error: tagsError, addTag } = useTags();
  const { transactions, addTransaction, updateTransaction, deleteTransaction, error: transactionsError } = useTransactions();
  const { settings, error: settingsError } = useAppSettings();
  const [editing, setEditing] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const pageError = transactionsError || categoriesError || tagsError || settingsError;
  const currentMonth = formatMonthKey(new Date());
  const money = (value: number) => formatMoney(value, settings.currency);

  const editingTransaction = useMemo(
    () => transactions.find((item) => item.id === editing) ?? null,
    [transactions, editing]
  );

  const monthOptions = useMemo(() => {
    const months = new Set([currentMonth]);
    transactions.forEach((tx) => months.add(tx.date.slice(0, 7)));
    return Array.from(months).sort((a, b) => b.localeCompare(a));
  }, [transactions, currentMonth]);

  const filteredTransactions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return transactions.filter((tx) => {
      const category = categories.find((item) => item.id === tx.categoryId);
      const txTags = tx.tagIds
        .map((tagId) => tags.find((tag) => tag.id === tagId))
        .filter((tag): tag is NonNullable<typeof tag> => Boolean(tag));
      const categoryName = category?.name ?? (tx.type === 'income' ? 'entrée' : 'dépense');
      const searchableText = [
        categoryName,
        tx.note ?? '',
        tx.date,
        tx.amount.toString(),
        tx.type === 'income' ? 'entrée revenu income' : 'dépense sortie expense',
        ...txTags.map((tag) => tag.name),
      ]
        .join(' ')
        .toLowerCase();

      if (monthFilter !== 'all' && !tx.date.startsWith(monthFilter)) return false;
      if (normalizedSearch && !searchableText.includes(normalizedSearch)) return false;
      if (typeFilter !== 'all' && tx.type !== typeFilter) return false;
      if (categoryFilter === 'none' && tx.categoryId) return false;
      if (categoryFilter !== 'all' && categoryFilter !== 'none' && tx.categoryId !== categoryFilter) return false;
      if (tagFilter === 'none' && tx.tagIds.length > 0) return false;
      if (tagFilter !== 'all' && tagFilter !== 'none' && !tx.tagIds.includes(tagFilter)) return false;

      return true;
    });
  }, [categories, categoryFilter, monthFilter, search, tagFilter, tags, transactions, typeFilter]);

  const filteredIncome = filteredTransactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0);
  const filteredExpense = filteredTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0);
  const filteredBalance = filteredIncome - filteredExpense;
  const filteredBalanceTone = filteredBalance >= 0 ? 'text-emerald-300' : 'text-rose-300';
  const hasFilters = search.trim() !== '' || monthFilter !== 'all' || typeFilter !== 'all' || categoryFilter !== 'all' || tagFilter !== 'all';
  const expenseCount = transactions.filter((tx) => tx.type === 'expense').length;
  const incomeCount = transactions.filter((tx) => tx.type === 'income').length;

  const resetFilters = () => {
    setSearch('');
    setMonthFilter('all');
    setTypeFilter('all');
    setCategoryFilter('all');
    setTagFilter('all');
  };

  const buildTransactionPayload = async (payload: TransactionFormPayload) => {
    const tagIds = [...payload.tagIds];
    const normalizedNames = payload.tagNames
      .map((name) => name.trim())
      .filter((name, index, names) => name && names.findIndex((item) => item.toLowerCase() === name.toLowerCase()) === index);

    for (const name of normalizedNames) {
      const existing = tags.find((tag) => tag.name.toLowerCase() === name.toLowerCase());
      if (existing) {
        if (!tagIds.includes(existing.id)) tagIds.push(existing.id);
      } else {
        const created = await addTag({ name, categoryId: payload.categoryId ?? '' });
        if (created) tagIds.push(created.id);
      }
    }

    return {
      amount: payload.amount,
      type: payload.type,
      date: payload.date,
      categoryId: payload.categoryId,
      tagIds,
      note: payload.note,
    };
  };

  return (
    <main className="page-shell">
      <div className="page-inner">
        {pageError ? (
          <div className="rounded-2xl border border-rose-900/70 bg-rose-950 p-4 text-sm text-rose-200">
            {pageError}
          </div>
        ) : null}

        <section className="overflow-hidden rounded-2xl border border-brand-500/20 bg-slate-900 shadow-sm">
          <div className="bg-brand-700 px-4 py-5 text-white min-[380px]:px-5">
            <AppLogo className="mb-1 text-brand-100" markClassName="border-white/25" textClassName="text-brand-100" />
            <div className="mt-1 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-xl font-semibold leading-tight text-white min-[380px]:text-2xl">Ajouter une opération</h1>
                <p className="mt-2 text-sm leading-6 text-brand-100">
                  Un montant, une date, et c'est suffisant. Les détails restent optionnels.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:w-56">
                <div className="rounded-2xl bg-slate-950/45 p-3">
                  <p className="text-xs font-medium text-brand-100">Entrées</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-200">{incomeCount}</p>
                </div>
                <div className="rounded-2xl bg-slate-950/45 p-3">
                  <p className="text-xs font-medium text-brand-100">Sorties</p>
                  <p className="mt-1 text-lg font-semibold text-rose-200">{expenseCount}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 min-[380px]:p-5">
            <TransactionForm
              categories={categories}
              tags={tags}
              loading={loadingCategories || loadingTags}
              initialValue={null}
              onSave={async (payload) => {
                const transactionPayload = await buildTransactionPayload(payload);
                await addTransaction(transactionPayload);
              }}
              onCancel={() => setEditing(null)}
            />
          </div>
        </section>

        <section className="section-card">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100">Historique</h2>
              <p className="mt-2 text-sm text-slate-400">
                {filteredTransactions.length}/{transactions.length} opération{transactions.length > 1 ? 's' : ''} affichée
                {filteredTransactions.length > 1 ? 's' : ''}
              </p>
            </div>
            {hasFilters ? (
              <button type="button" onClick={resetFilters} className="secondary-button w-full px-3 py-2 sm:w-auto">
                Réinitialiser
              </button>
            ) : null}
          </div>

          <div className="mb-4 rounded-2xl border border-slate-800 bg-slate-950 p-3">
            <label className="block">
              <span className="sr-only">Rechercher dans l'historique</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher..."
                className="w-full px-4 py-3"
              />
            </label>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <label>
                <span className="sr-only">Mois</span>
                <select value={monthFilter} onChange={(event) => setMonthFilter(event.target.value)} className="w-full px-4 py-3">
                  <option value="all">Tous les mois</option>
                  {monthOptions.map((month) => (
                    <option key={month} value={month}>
                      {formatMonthLabel(month)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="sr-only">Type</span>
                <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as typeof typeFilter)} className="w-full px-4 py-3">
                  <option value="all">Tous les types</option>
                  <option value="expense">Sorties</option>
                  <option value="income">Entrées</option>
                </select>
              </label>
              <label>
                <span className="sr-only">Catégorie</span>
                <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} className="w-full px-4 py-3">
                  <option value="all">Toutes les catégories</option>
                  <option value="none">Sans catégorie</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="sr-only">Tag</span>
                <select value={tagFilter} onChange={(event) => setTagFilter(event.target.value)} className="w-full px-4 py-3">
                  <option value="all">Tous les tags</option>
                  <option value="none">Sans tag</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-3 grid gap-2 min-[420px]:grid-cols-2 sm:grid-cols-4">
              <div className="rounded-2xl bg-slate-900 p-3">
                <p className="text-xs font-medium text-slate-400">Entrées</p>
                <p className="mt-1 truncate text-sm font-semibold text-emerald-300">{money(filteredIncome)}</p>
              </div>
              <div className="rounded-2xl bg-slate-900 p-3">
                <p className="text-xs font-medium text-slate-400">Sorties</p>
                <p className="mt-1 truncate text-sm font-semibold text-rose-300">{money(filteredExpense)}</p>
              </div>
              <div className="rounded-2xl bg-slate-900 p-3">
                <p className="text-xs font-medium text-slate-400">Solde</p>
                <p className={`mt-1 truncate text-sm font-semibold ${filteredBalanceTone}`}>{money(filteredBalance)}</p>
              </div>
              <div className="rounded-2xl bg-slate-900 p-3">
                <p className="text-xs font-medium text-slate-400">Opérations</p>
                <p className="mt-1 text-sm font-semibold text-slate-100">{filteredTransactions.length}</p>
              </div>
            </div>
          </div>

          <TransactionList
            transactions={filteredTransactions}
            categories={categories}
            tags={tags}
            onEdit={(id) => setEditing(id)}
            onDelete={deleteTransaction}
            emptyLabel={hasFilters ? 'Aucune transaction ne correspond à ces filtres.' : undefined}
          />
        </section>
      </div>

      {editingTransaction ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/75 px-4 py-6 sm:items-center">
          <div
            className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-4 shadow-xl min-[380px]:p-5"
            role="dialog"
            aria-modal="true"
            aria-label="Modifier la transaction"
          >
            <div className="mb-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="eyebrow">Modification</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-100">Modifier la transaction</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="secondary-button shrink-0 px-3 py-2">
                Fermer
              </button>
            </div>

            <TransactionForm
              categories={categories}
              tags={tags}
              loading={loadingCategories || loadingTags}
              initialValue={editingTransaction}
              onSave={async (payload) => {
                const transactionPayload = await buildTransactionPayload(payload);
                await updateTransaction(editingTransaction.id, transactionPayload);
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
            />
          </div>
        </div>
      ) : null}

      <BottomNavigation active="/transactions" />
      <div className="page-inner mt-5">
        <AppFooter />
      </div>
    </main>
  );
}
